<template>
    <div class="layout-sidebar" :class="{ 'layout-sidebar-expanded': isExpanded }">
        <!--
            Corporate desktop collapse/expand control (2026 chrome).
            Keep this OUTSIDE the scrolling `.layout-menu-container` so it
            remains fixed in the 5rem top chrome band while the menu scrolls.
        -->
        <div
            v-if="isCorporate && !($isMobile || $isTablet)"
            class="sidebar-chrome-toggle tw:flex tw:items-center"
            :class="isExpanded ? 'tw:justify-start' : 'tw:justify-center'"
        >
            <Button
                data-testid="sidebar-collapse-toggle"
                class="tw:bg-transparent tw:border-none tw:text-[var(--text-color)]"
                :icon="isExpanded ? 'uil-multiply' : 'uil-bars'"
                size="large"
                :aria-label="isExpanded ? 'Collapse menu' : 'Expand menu'"
                @click="toggleSidebarSummary"
            />
        </div>

        <div class="layout-menu-container hide-scrollbar">
            <AppMenu v-if="!isCorporate && ($isMobile || $isTablet)" />
            <template v-else>
                <!--
                    Mobile-only close (X). Keep inside the scrolling container
                    so it participates in the mobile drawer layout.
                -->
                <div v-if="$isMobile || $isTablet" class="tw:flex tw:justify-end tw:items-center tw:-mb-2">
                    <Button
                        data-testid="mobile-sidebar-close"
                        class="tw:bg-transparent tw:border-none tw:text-[var(--text-color)]"
                        icon="uil-multiply"
                        size="large"
                        aria-label="Close menu"
                        @click="closeMenu"
                    />
                </div>

                <div
                    class="layout-sidebar-body tw:flex tw:flex-col"
                    :class="isCorporate ? 'tw:flex-1 tw:min-h-0' : 'tw:h-full'"
                >
                    <component
                        :is="resolvedComponent"
                        ref="sidebarComponentRef"
                        @update:expanded="handleExpansionUpdate"
                    />
                </div>
            </template>
        </div>
    </div>
</template>

<script setup>
import Button from 'primevue/button';
import { computed, ref, watch } from 'vue';
import InstitutionSettingsSidebar from '@/components/administration/institution-settings/InstitutionSettingsSidebar.vue';
import appSidebar from '@/components/app/AppSidebar.vue';
import GroupSidebar from '@/components/groups/GroupSidebar.vue';
import HelpSidebar from '@/components/help/HelpSidebar.vue';
import InboxSidebar from '@/components/inbox/InboxSidebar.vue';
import EditPagesSidebar from '@/components/pages/EditPagesSidebar.vue';
import ReportsSidebar from '@/components/reports/ReportsSidebar.vue';
import DashboardSidebar from '@/components/sidebar/DashboardSidebar.vue';
import SidebarMenu from '@/components/sidebar/SidebarMenu.vue';
import UserSidebar from '@/components/users/UserSidebar.vue';
import { useEdverumClient } from '@/composables/useEdverumClient.js';
import AppMenu from './AppMenu.vue';
import { useLayout } from './composables/layout.js';

const { layoutState, closeMenu, toggleSidebarSummary } = useLayout();

// Corporate clients keep the role-specific sidebar (CorporateAdminSidebar /
// CorporateStudentSidebar) at every viewport, rendered inside the existing
// mobile drawer. The generic AppMenu only applies to non-corporate clients on
// mobile/tablet, where it is the intended permission-driven mobile menu.
const { isCorporate } = useEdverumClient();

const props = defineProps({
    sidebarComponent: {
        type: String,
        default: undefined,
    },
});

const isExpanded = ref(false);
const sidebarComponentRef = ref(null);

// Map kebab-case string names to actual component objects
const components = {
    'app-sidebar': appSidebar,
    'institution-settings-sidebar': InstitutionSettingsSidebar,
    SidebarMenu,
    'user-sidebar': UserSidebar,
    'help-sidebar': HelpSidebar,
    'reports-sidebar': ReportsSidebar,
    'edit-pages-sidebar': EditPagesSidebar,
    'group-sidebar': GroupSidebar,
    'pages-sidebar': DashboardSidebar,
    'inbox-sidebar': InboxSidebar,
};

// Resolve the component based on the prop
const resolvedComponent = computed(() => {
    return props.sidebarComponent ? components[props.sidebarComponent] : null;
});

// Handle expansion state from app-sidebar
const handleExpansionUpdate = expanded => {
    isExpanded.value = expanded;
};

// Summary (icon rail) is a desktop affordance. On mobile/tablet the drawer is
// always expanded, so closing it must NOT toggle the shared summary state -
// otherwise reopening the drawer would show an icon-only menu and the desktop
// sidebar would be left collapsed after a resize.
const isDesktopViewport = () => window.innerWidth >= 1200;

// Watch for sidebar being hidden and reset the local layout width class. These
// watchers never touch the shared summary flag: only the top-bar control or a
// folder icon in the collapsed rail changes it.
watch(
    () => layoutState.staticMenuDesktopInactive,
    isInactive => {
        if (!isDesktopViewport()) {
            return;
        }

        if (isInactive && isExpanded.value) {
            // Sidebar is being hidden, reset expansion
            isExpanded.value = false;
        } else if (!isInactive) {
            // Sidebar is visible again: the child will not re-emit, so re-derive
            // the local width class from the shared mode.
            isExpanded.value = !layoutState.sidebarSummaryMode;
        }
    }
);

// Also watch for overlay menu being closed
watch(
    () => layoutState.overlayMenuActive,
    isActive => {
        if (!isActive && isExpanded.value && isDesktopViewport()) {
            // Closing the drawer/overlay on desktop keeps the shared mode as
            // the source of truth for the local width class.
            isExpanded.value = !layoutState.sidebarSummaryMode;
        }
    }
);

// Also watch for mobile menu being closed
watch(
    () => layoutState.staticMenuMobileActive,
    isActive => {
        if (!isActive && isExpanded.value && isDesktopViewport()) {
            // Closing the drawer/overlay on desktop keeps the shared mode as
            // the source of truth for the local width class.
            isExpanded.value = !layoutState.sidebarSummaryMode;
        }
    }
);
</script>

<style lang="scss" scoped>
.layout-sidebar {
    padding: 1rem;
}

/* Apply this to the element you want to hide the scrollbar for */
.hide-scrollbar {
    -ms-overflow-style: none;  /* IE and Edge */
    scrollbar-width: none;     /* Firefox */
}

/* For Chrome, Safari, and Opera */
.hide-scrollbar::-webkit-scrollbar {
    display: none;             /* Hide the scrollbar */
    width: 0px;                /* Optional: remove scrollbar space */
}
</style>
